/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import Imgix from 'js/components/Imgix';
import { FormattedMessage } from 'js/lib/coursera.react-intl';

import { Typography2, breakpoints } from '@coursera/cds-core';
import { useVisibilityTracker } from '@coursera/event-pulse/react';

import type { EventingType } from 'bundles/unified-description-page-common/contexts/EventingContext';

import _t from 'i18n!nls/cds-labs';

const SQUARE_IMAGE_DIMENSION = 64;

export type TestimonialItemType = {
  imageSrc?: string | null;
  shouldUseImgix?: boolean;
  learnerSince?: number | null;
  name?: string | null;
  quote?: string | null;
  trackingIndex?: number | null;
  eventingData?: EventingType;
  productName?: string | null;
  isInvertedTextColor?: boolean;
  forceUseDesktopStyles?: boolean;
};

const styles = {
  container: css`
    border: 1px solid var(--cds-color-neutral-stroke-primary-weak);
    padding: var(--cds-spacing-200);
    border-radius: var(--cds-border-radius-100);
    height: 100%;
  `,
  mobileContainer: css`
    ${breakpoints.down('sm')} {
      padding: var(--cds-spacing-200) var(--cds-spacing-600);
      margin: 0 var(--cds-spacing-100);
    }
  `,
  header: css`
    display: flex;
    align-items: center;
    margin-bottom: var(--cds-spacing-100);
  `,
  image: css`
    margin-right: var(--cds-spacing-200);
    border-radius: 100%;
  `,
  headerTextContainer: css`
    display: flex;
    flex-direction: column;
    gap: calc(var(--cds-spacing-50) / 2);
  `,
};

const TestimonialItem = ({
  imageSrc,
  shouldUseImgix = true,
  learnerSince,
  name,
  quote,
  trackingIndex,
  eventingData,
  productName,
  isInvertedTextColor,
  forceUseDesktopStyles = false,
}: TestimonialItemType) => {
  const visibilityTrackingRef: React.MutableRefObject<HTMLDivElement | null> = useVisibilityTracker(
    'view_testimonial_card',
    {
      testimonialCard: {
        author: name ?? '',
        text: quote ?? '',
        ...(trackingIndex !== undefined && trackingIndex !== null
          ? {
              index: trackingIndex,
            }
          : {}),
      },
      ...eventingData,
    }
  );

  if (!quote || !name) {
    return null;
  }

  return (
    <div css={[styles.container, !forceUseDesktopStyles && styles.mobileContainer]} ref={visibilityTrackingRef}>
      <div css={styles.header}>
        {imageSrc && shouldUseImgix && (
          <Imgix
            src={imageSrc}
            alt=""
            css={styles.image}
            maxWidth={SQUARE_IMAGE_DIMENSION}
            maxHeight={SQUARE_IMAGE_DIMENSION}
            imgParams={{ fit: 'crop' }}
          />
        )}
        {imageSrc && !shouldUseImgix && (
          <img
            src={imageSrc}
            alt=""
            css={styles.image}
            width={SQUARE_IMAGE_DIMENSION}
            height={SQUARE_IMAGE_DIMENSION}
          />
        )}
        <div css={styles.headerTextContainer}>
          <Typography2 variant="subtitleMedium" component="div" color={isInvertedTextColor ? 'invertBody' : 'inherit'}>
            {name}
          </Typography2>
          {Boolean(learnerSince) && (
            <Typography2
              variant="bodyPrimary"
              component="div"
              color={isInvertedTextColor ? 'invertBody' : 'supportText'}
            >
              {_t('Learner since #{learnerSince}', { learnerSince })}
            </Typography2>
          )}
          {productName && (
            <Typography2
              variant="bodySecondary"
              component="div"
              color={isInvertedTextColor ? 'invertBody' : 'supportText'}
            >
              {productName}
            </Typography2>
          )}
        </div>
      </div>
      <Typography2 variant="bodyPrimary" component="div" color={isInvertedTextColor ? 'invertBody' : 'supportText'}>
        <FormattedMessage message={_t('"{text}"')} text={quote} />
      </Typography2>
    </div>
  );
};

export default TestimonialItem;
